import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import { useTranslation } from "react-i18next";
import CorridorMap from "@/components/home/CorridorMap";
import WaitlistCTA from "@/components/home/WaitlistCTA";
import RatesMarquee from "@/components/home/RatesMarquee";

const SEND_FROM = [
  { flag: "🇬🇧", name: "United Kingdom", code: "GBP" },
  { flag: "🇺🇸", name: "United States", code: "USD" },
  { flag: "🇪🇺", name: "Eurozone (SEPA)", code: "EUR" },
  { flag: "🇨🇦", name: "Canada", code: "CAD" },
  { flag: "🇦🇺", name: "Australia", code: "AUD" },
  { flag: "🇦🇪", name: "UAE", code: "AED" },
  { flag: "🇨🇭", name: "Switzerland", code: "CHF" },
  { flag: "🇳🇴", name: "Norway", code: "NOK" },
  { flag: "🇸🇪", name: "Sweden", code: "SEK" },
];

const RECEIVE_IN = [
  { flag: "🇳🇬", name: "Nigeria", code: "NGN", methods: "Bank · MoMo · Cash" },
  { flag: "🇰🇪", name: "Kenya", code: "KES", methods: "M-Pesa · Bank · Cash" },
  { flag: "🇬🇭", name: "Ghana", code: "GHS", methods: "MoMo · Bank · Cash" },
  { flag: "🇺🇬", name: "Uganda", code: "UGX", methods: "MTN · Airtel · Bank" },
  { flag: "🇹🇿", name: "Tanzania", code: "TZS", methods: "Tigo · M-Pesa · Bank" },
  { flag: "🇿🇦", name: "South Africa", code: "ZAR", methods: "Bank · Cash" },
  { flag: "🇷🇼", name: "Rwanda", code: "RWF", methods: "MTN · Airtel · Bank" },
  { flag: "🇸🇳", name: "Senegal", code: "XOF", methods: "Wave · Orange · Bank" },
  { flag: "🇨🇮", name: "Côte d'Ivoire", code: "XOF", methods: "MTN · Orange · Bank" },
  { flag: "🇲🇦", name: "Morocco", code: "MAD", methods: "Bank · Cash" },
  { flag: "🇪🇬", name: "Egypt", code: "EGP", methods: "Bank · InstaPay · Cash" },
  { flag: "🇪🇹", name: "Ethiopia", code: "ETB", methods: "Bank · Telebirr" },
  { flag: "🇿🇲", name: "Zambia", code: "ZMW", methods: "MoMo · Bank" },
  { flag: "🇲🇼", name: "Malawi", code: "MWK", methods: "TNM · Airtel · Bank" },
  { flag: "🇧🇯", name: "Benin", code: "XOF", methods: "MTN · Moov · Bank" },
  { flag: "🇹🇬", name: "Togo", code: "XOF", methods: "Flooz · TMoney · Bank" },
];

export default function Countries() {
  const { t } = useTranslation();
  return (
    <>
      <SEO
        slug="countries"
        title="Countries · 42 corridors and counting — Instantxfer CashApp"
        description="Direct rails on both sides: bank, MTN MoMo, M-Pesa, Airtel, Orange, cash pickup. Send from London payroll to a rural Rwandan agent in minutes."
      />
      <PageHero
        eyebrow={t("pageHero.countries.eyebrow")}
        title={t("pageHero.countries.title")}
        italic={t("pageHero.countries.italic")}
        body={t("pageHero.countries.body")}
        testId="countries-hero"
      />

      <CorridorMap />

      <section className="py-24 md:py-32 bg-surface">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="label-eyebrow">Send from</div>
              <h2 className="mt-3 font-display font-medium text-forest text-3xl md:text-4xl tracking-tightest">
                Origin markets
              </h2>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SEND_FROM.map((c) => (
                  <div
                    key={c.code}
                    className="card-flat p-4 flex items-center gap-4"
                    data-testid={`send-from-${c.code}`}
                  >
                    <div className="text-2xl">{c.flag}</div>
                    <div className="flex-1">
                      <div className="font-display text-forest">{c.name}</div>
                      <div className="text-xs text-ink/50 font-mono tracking-wider">{c.code}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="label-eyebrow">Receive in</div>
              <h2 className="mt-3 font-display font-medium text-forest text-3xl md:text-4xl tracking-tightest">
                Payout markets
              </h2>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {RECEIVE_IN.map((c) => (
                  <div
                    key={`${c.code}-${c.name}`}
                    className="card-flat p-4 flex items-center gap-4"
                    data-testid={`receive-in-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    <div className="text-2xl">{c.flag}</div>
                    <div className="flex-1">
                      <div className="font-display text-forest">{c.name}</div>
                      <div className="text-xs text-ink/60">{c.methods}</div>
                    </div>
                    <div className="font-mono text-xs text-ink/50">{c.code}</div>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm text-ink/60">
                Don&apos;t see your corridor? Tell us — new markets ship every quarter.
              </p>
            </div>
          </div>
        </div>
      </section>

      <RatesMarquee variant="dark" />
      <WaitlistCTA />
    </>
  );
}
